/**
 * Analitik — Hard-Data Analytics Dashboard
 *
 * Veri kaynağı: GET /api/analytics/dashboard?range=7|30|90
 *   → KPI'lar, funnel, hotspot tabloları, body insights, timeline, integration health
 *
 * CSV: GET /api/analytics/export.csv?range=&type=queries|suggestions
 *
 * Felsefe: SADECE ÖLÇÜLMÜŞ VERİ. Tahmin, "potansiyel ROI", "önlenen iade" vb.
 * uydurma metrik YOK. Boş collection → sayfa EmptyState gösterir, 0'ları
 * fake sayı gibi süslemez.
 *
 * UX: Tüm veri bloklarını alt alta göstermek yerine beş mantıksal tab'a
 * bölünmüştür. Her tab bir soruya cevap verir:
 *   • Genel Bakış  → "Bu dönemde ne oldu?" (KPI + timeline)
 *   • Dönüşüm       → "Ziyaretçiler widget'ı nasıl kullanıyor?" (funnel + confidence)
 *   • Ürünler       → "Hangi SKU'lar aksiyon ister?" (hotspots)
 *   • Ziyaretçi    → "Kime hizmet veriyoruz?" (body profile)
 *   • Entegrasyon  → "Kurulum sağlıklı mı?" (methods + hata trendi)
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    BarChart3, Download, AlertTriangle, Flame, TrendingDown, Package,
    Loader2, Users, MousePointerClick, CheckCircle2, ShieldAlert,
    Activity, Gauge, Signal, Info, UserCheck, LayoutGrid, PlugZap,
    LineChart as LineChartIcon, Bot, FlaskConical, Copy, ArrowRight,
    Shirt, Ruler, Clock,
} from "lucide-react";
import {
    Bar, BarChart, CartesianGrid, Line, LineChart, Area, AreaChart,
    ResponsiveContainer, Tooltip, XAxis, YAxis, Legend,
} from "recharts";
import { api, API_BASE } from "@/lib/api";
import {
    PageHeader, SecondaryButton, Card, CardHeader, Eyebrow,
} from "@/components/design-system";

const RANGES = [
    { value: 7, label: "7 gün" },
    { value: 30, label: "30 gün" },
    { value: 90, label: "90 gün" },
];

const TABS = [
    { key: "overview", label: "Genel Bakış", icon: BarChart3 },
    { key: "traffic", label: "Trafik", icon: Gauge },
    { key: "conversion", label: "Dönüşüm", icon: LineChartIcon },
    { key: "products", label: "Ürünler", icon: LayoutGrid },
    { key: "visitors", label: "Ziyaretçi", icon: UserCheck },
    { key: "integration", label: "Entegrasyon", icon: PlugZap },
];

// Traffic tab is anchored to the current calendar month — the range
// selector (7/30/90) is hidden when it's active, and the fetch hits a
// different endpoint (/analytics/traffic) that owns its own period.
const TAB_IGNORES_RANGE = new Set(["traffic"]);

export default function Analytics() {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlRange = Number(searchParams.get("range")) || 7;
    const urlTab = TABS.some((t) => t.key === searchParams.get("tab"))
        ? searchParams.get("tab")
        : "overview";

    const [range, setRange] = useState([7, 30, 90].includes(urlRange) ? urlRange : 7);
    const [tab, setTab] = useState(urlTab);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Traffic tab has its own payload (anchored to current calendar month).
    // We cache it alongside the main dashboard data to avoid re-fetching
    // when the user tabs between Trafik and other tabs.
    const [traffic, setTraffic] = useState(null);
    const [trafficLoading, setTrafficLoading] = useState(false);
    const [trafficError, setTrafficError] = useState(null);

    // URL sync (deep-linkable range + tab). Avoid loops: only write when
    // the value actually differs.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (String(next.get("range") || "") !== String(range)) next.set("range", String(range));
        if (String(next.get("tab") || "") !== tab) next.set("tab", tab);
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range, tab]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.get(`/analytics/dashboard?range=${range}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => { if (!cancelled) setError("Analitik verisi alınamadı."); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [range]);

    // Traffic data: fetch when the Trafik tab is first opened, cache in
    // state. Current calendar month only — not affected by range selector.
    // IMPORTANT: `trafficLoading` is NOT in deps — including it causes
    // setTrafficLoading(true) to re-trigger the effect, whose cleanup then
    // cancels the in-flight fetch before it can resolve.
    useEffect(() => {
        if (tab !== "traffic" || traffic) return;
        let cancelled = false;
        setTrafficLoading(true);
        setTrafficError(null);
        api.get("/analytics/traffic")
            .then((r) => { if (!cancelled) setTraffic(r.data); })
            .catch(() => { if (!cancelled) setTrafficError("Trafik verisi alınamadı."); })
            .finally(() => { if (!cancelled) setTrafficLoading(false); });
        return () => { cancelled = true; };
    }, [tab, traffic]);

    const downloadCsv = (type) => {
        const url = `${API_BASE}/analytics/export.csv?range=${range}&type=${type}`;
        window.open(url, "_blank");
    };

    const kpis = data?.kpis;
    const empty = kpis && !kpis.totalQueries && !kpis.modalOpens && !kpis.suggestions;
    // Traffic tab owns a different period (current month, not 7/30/90).
    // Hide the range selector and CSV buttons when it's the active tab.
    const isTrafficTab = tab === "traffic";

    return (
        <div className="space-y-6" data-testid="analytics-page">
            <PageHeader
                icon={BarChart3}
                title="Analitik"
                description="Widget etkileşimleri ve beden önerileri — yalnızca gerçek ölçümlenmiş veri."
                actions={
                    !isTrafficTab && (
                        <>
                            <RangeTabs value={range} onChange={setRange} />
                            <SecondaryButton
                                size="sm"
                                onClick={() => downloadCsv("queries")}
                                data-testid="analytics-csv-queries"
                            >
                                <Download className="h-3.5 w-3.5" />
                                Sorgular (CSV)
                            </SecondaryButton>
                            <SecondaryButton
                                size="sm"
                                onClick={() => downloadCsv("suggestions")}
                                data-testid="analytics-csv-suggestions"
                            >
                                <Download className="h-3.5 w-3.5" />
                                Öneriler (CSV)
                            </SecondaryButton>
                        </>
                    )
                }
            />

            {range === 90 && !loading && !isTrafficTab && (
                <div className="rounded-md border border-border bg-muted/30 px-4 py-2.5 flex items-start gap-2 text-xs" data-testid="analytics-retention-hint">
                    <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                    <span className="text-muted-foreground leading-relaxed">
                        Log'lar 30 gün sonra otomatik silinir — 90 günlük görünümde yalnızca son 30 güne ait veri yer alır.
                    </span>
                </div>
            )}

            {/* Traffic tab bypasses the main dashboard fetch — it owns its
                own payload. Always render TabStrip first so user can navigate
                regardless of loading state. */}
            {isTrafficTab ? (
                <>
                    <TabStrip value={tab} onChange={setTab} />
                    {trafficLoading && !traffic && <LoadingState />}
                    {trafficError && !traffic && (
                        <Card className="p-10 grid place-items-center text-center" data-testid="analytics-traffic-error">
                            <AlertTriangle className="h-8 w-8 text-destructive mb-2" />
                            <div className="font-display font-semibold">{trafficError}</div>
                        </Card>
                    )}
                    {traffic && <TrafficTab data={traffic} />}
                </>
            ) : (
                <>
                    {loading && <LoadingState />}

                    {!loading && error && (
                        <Card className="p-10 grid place-items-center text-center" data-testid="analytics-error">
                            <AlertTriangle className="h-8 w-8 text-destructive mb-2" />
                            <div className="font-display font-semibold">{error}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                                Sayfayı yenile veya daha sonra tekrar dene.
                            </div>
                        </Card>
                    )}

                    {!loading && data && (
                        <>
                            {empty && <ZeroDataNotice />}
                            <TabStrip value={tab} onChange={setTab} />
                            {tab === "overview" && <OverviewTab data={data} />}
                            {tab === "conversion" && <ConversionTab data={data} />}
                            {tab === "products" && <ProductsTab data={data} />}
                            {tab === "visitors" && <VisitorsTab data={data} />}
                            {tab === "integration" && <IntegrationTab data={data} />}
                        </>
                    )}
                </>
            )}
        </div>
    );
}

/* ────────────────── Range + Tab Strips ────────────────── */

const RangeTabs = ({ value, onChange }) => (
    <div className="inline-flex items-center rounded-md border border-border bg-surface p-0.5" data-testid="analytics-range-tabs">
        {RANGES.map((r) => (
            <button
                key={r.value}
                onClick={() => onChange(r.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                    value === r.value
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`analytics-range-${r.value}`}
            >
                {r.label}
            </button>
        ))}
    </div>
);

const TabStrip = ({ value, onChange }) => (
    <div className="border-b border-border" data-testid="analytics-tabs">
        <div className="flex items-center gap-1 overflow-x-auto -mb-px">
            {TABS.map((t) => {
                const Icon = t.icon;
                const active = value === t.key;
                return (
                    <button
                        key={t.key}
                        onClick={() => onChange(t.key)}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
                            active
                                ? "border-foreground text-foreground"
                                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                        }`}
                        data-testid={`analytics-tab-${t.key}`}
                        aria-selected={active}
                        role="tab"
                    >
                        <Icon className="h-3.5 w-3.5" />
                        {t.label}
                    </button>
                );
            })}
        </div>
    </div>
);

/* ────────────────── Shared States ────────────────── */

const LoadingState = () => (
    <div className="grid place-items-center py-24" data-testid="analytics-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
);

const ZeroDataNotice = () => (
    <Card className="p-5 flex items-start gap-3" data-testid="analytics-empty">
        <div className="h-9 w-9 rounded-md bg-muted/40 grid place-items-center shrink-0">
            <Activity className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0">
            <div className="font-display font-semibold text-sm">Bu aralıkta henüz etkileşim yok</div>
            <div className="text-xs text-muted-foreground mt-0.5">
                Widget sitenizde kuruluyken ziyaretçi ürün sayfasına girdikçe veriler burada toplanır.
                Zaman aralığını değiştirebilir veya kurulum durumunu kontrol edebilirsiniz.
            </div>
        </div>
    </Card>
);

/* ────────────────── Tab: Overview ────────────────── */

const OverviewTab = ({ data }) => (
    <div className="space-y-5" data-testid="analytics-tab-overview-panel">
        <KpiGrid kpis={data.kpis} />
        <TimelineSection timeline={data.timeline} />
    </div>
);

/* ────────────────── Tab: Traffic (bu ayki kota şeffaflığı) ────────────────── */

/**
 * TrafficTab — "Bu ay kaç tane modal açıldı, kotamın ne kadarı doldu"
 * sorusunun tek yeri. Veri kaynağı: GET /api/analytics/traffic (ayrı
 * endpoint, kendi takvim-ayı penceresi vardır).
 *
 * Felsefe: Müşteri "neyi faturalandırıyorsun, neyi faturalandırmıyorsun"ı
 * tek bakışta görebilmeli. Dört kutuyu yan yana koyuyoruz:
 *   • Sayıldı (billed)           → faturaya giden
 *   • Bot filtresi (bot)         → UA bazlı crawler/headless
 *   • Test ortamı (test)         → ?sayz_test=1 veya /app/ panel preview
 *   • Dedup (duplicate)          → aynı session + sku + widget, <5dk
 *
 * Tek bir eksik rakam bırakmıyoruz. Projeksiyon kartı da dahil: "Bu hızla
 * ayın 24'ünde biter" tahmini → kullanıcı yıl-sonu kampanyalarına hazırlanır.
 */
const TrafficTab = ({ data }) => {
    const quota = data?.quota || { limit: 200000, used: 0, pct: 0, remaining: 200000 };
    const period = data?.period || {};
    const projection = data?.projection || {};
    const breakdown = data?.breakdown || {};
    const filtered = data?.filtered || {};
    const timeline = data?.timeline || [];

    const periodLabel = useMemo(() => {
        if (!period.start) return "—";
        const d = new Date(period.start);
        return d.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
    }, [period.start]);

    const pct = Math.min(100, Math.max(0, quota.pct || 0));
    const barTone = pct >= 90 ? "bg-destructive"
        : pct >= 70 ? "bg-amber-500"
        : "bg-emerald-500";

    return (
        <div className="space-y-5" data-testid="analytics-tab-traffic-panel">
            {/* ─ Ana KPI: büyük sayaç + progress bar ────────────────────── */}
            <Card data-testid="traffic-main-kpi">
                <div className="p-5 md:p-6 space-y-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <Eyebrow>Faturalanabilir etkileşim · {periodLabel}</Eyebrow>
                            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                                <span className="font-display text-4xl md:text-5xl font-black tracking-tighter num">
                                    {fmtInt(quota.used)}
                                </span>
                                <span className="text-base text-muted-foreground num">
                                    / {fmtInt(quota.limit)} hak
                                </span>
                            </div>
                            <div className="mt-1 text-sm text-muted-foreground">
                                %{pct.toFixed(1)} kullanıldı · {fmtInt(quota.remaining)} kaldı
                            </div>
                        </div>
                        <div className="text-right min-w-0">
                            <Eyebrow>Dönem</Eyebrow>
                            <div className="mt-2 flex items-center justify-end gap-1.5 text-sm">
                                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="font-display font-semibold num">
                                    {period.daysElapsed || 0}/{period.daysInPeriod || 30} gün
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 num">
                                {period.daysRemaining || 0} gün kaldı
                            </div>
                        </div>
                    </div>
                    <div className="relative h-2 w-full rounded-full bg-foreground/[0.06] overflow-hidden" data-testid="traffic-progress-bar">
                        <div
                            className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${barTone}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                </div>
            </Card>

            {/* ─ Projeksiyon + Widget breakdown ─────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <ProjectionCard projection={projection} quota={quota} period={period} />
                <WidgetBreakdownCard breakdown={breakdown} total={quota.used} />
            </div>

            {/* ─ Filtered-out breakdown (bot/test/dedup) ───────────────── */}
            <FilteredCard filtered={filtered} />

            {/* ─ Günlük timeline chart ─────────────────────────────────── */}
            <TrafficTimelineCard timeline={timeline} />

            {/* ─ "Ne sayılır" özet ─────────────────────────────────────── */}
            <UsagePolicyCard />
        </div>
    );
};

/* ── Projection card ─────────────────────────────────────────────────── */
const ProjectionCard = ({ projection, quota, period }) => {
    const avg = projection?.dailyAvg || 0;
    const projected = projection?.projectedTotal || 0;
    const depleteOn = projection?.depleteOn;
    const depleteDate = depleteOn
        ? new Date(depleteOn).toLocaleDateString("tr-TR", { day: "2-digit", month: "long" })
        : null;
    const willExceed = projected > (quota?.limit || 0);

    return (
        <Card data-testid="traffic-projection-card">
            <CardHeader
                title="Projeksiyon"
                desc="Mevcut hızla ay sonunda nerede olacaksın"
            />
            <div className="p-5 space-y-4">
                <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                        <Eyebrow>Günlük ortalama</Eyebrow>
                        <div className="mt-1 font-display text-2xl font-black tracking-tighter num">
                            {fmtInt(Math.round(avg))}
                        </div>
                    </div>
                    <div className="text-right min-w-0">
                        <Eyebrow>Ay sonu tahmini</Eyebrow>
                        <div className={`mt-1 font-display text-2xl font-black tracking-tighter num ${willExceed ? "text-destructive" : "text-foreground"}`}>
                            {fmtInt(projected)}
                        </div>
                    </div>
                </div>
                {depleteOn ? (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2" data-testid="traffic-deplete-warning">
                        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                        <div className="min-w-0 text-xs">
                            <div className="font-semibold text-amber-700 dark:text-amber-400">
                                Bu hızla <span className="num">{depleteDate}</span> civarında kotayı doldurursun
                            </div>
                            <div className="text-muted-foreground mt-0.5 leading-relaxed">
                                Dönemin bitmesine {period?.daysRemaining || 0} gün var. Daha yüksek bir aylık trafik için bizimle iletişime geç.
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-start gap-2" data-testid="traffic-pace-ok">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                        <div className="min-w-0 text-xs">
                            <div className="font-semibold text-emerald-700 dark:text-emerald-400">
                                Mevcut hızda kota ay sonuna rahat yetiyor
                            </div>
                            <div className="text-muted-foreground mt-0.5">
                                Tahmini kullanım: %{quota?.limit ? Math.round((projected / quota.limit) * 100) : 0} · Rahat marj var.
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

/* ── Widget breakdown (Beden Tablosu vs Find My Size) ────────────────── */
const WidgetBreakdownCard = ({ breakdown, total }) => {
    const st = breakdown?.sizeTableOpens || 0;
    const fms = breakdown?.findMySizeOpens || 0;
    const stPct = total ? (st / total) * 100 : 0;
    const fmsPct = total ? (fms / total) * 100 : 0;

    return (
        <Card data-testid="traffic-breakdown-card">
            <CardHeader
                title="Widget dağılımı"
                desc="Faturalanan modal açılışlarının hangi widget'tan geldiği"
            />
            <div className="p-5 space-y-4">
                <BreakdownRow
                    icon={Shirt}
                    label="Beden Tablosu"
                    count={st}
                    pct={stPct}
                    tone="bg-foreground"
                    testId="traffic-breakdown-size-table"
                />
                <BreakdownRow
                    icon={Ruler}
                    label="Bedenimi Bul"
                    count={fms}
                    pct={fmsPct}
                    tone="bg-foreground/70"
                    testId="traffic-breakdown-find-my-size"
                />
                {!total && (
                    <div className="text-xs text-muted-foreground pt-1">
                        Bu dönemde henüz faturalanabilir açılış yok.
                    </div>
                )}
            </div>
        </Card>
    );
};

const BreakdownRow = ({ icon: Icon, label, count, pct, tone, testId }) => (
    <div data-testid={testId}>
        <div className="flex items-center justify-between gap-3 mb-1.5">
            <div className="flex items-center gap-2 min-w-0">
                <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate">{label}</span>
            </div>
            <div className="flex items-baseline gap-2 shrink-0">
                <span className="font-display text-sm font-semibold num">{fmtInt(count)}</span>
                <span className="text-[11px] text-muted-foreground num">%{pct.toFixed(0)}</span>
            </div>
        </div>
        <div className="h-1 w-full rounded-full bg-foreground/[0.06] overflow-hidden">
            <div className={`h-full ${tone} transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>
    </div>
);

/* ── Filtered-out breakdown ──────────────────────────────────────────── */
const FilteredCard = ({ filtered }) => (
    <Card data-testid="traffic-filtered-card">
        <CardHeader
            title="Sayılmayan trafik — şeffaflık"
            desc="Faturalandırmadığımız etkileşimler, hangi filtreden geçtiğini açıkça gösteriyoruz"
        />
        <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FilterStat
                icon={Bot}
                label="Bot trafiği"
                count={filtered?.botCount || 0}
                description="User-Agent crawler/headless imzaları ile eşleşen istekler"
                testId="traffic-filter-bot"
            />
            <FilterStat
                icon={FlaskConical}
                label="Test ortamı"
                count={filtered?.testCount || 0}
                description="?sayz_test=1 taşıyan URL'lerden gelen açılışlar"
                testId="traffic-filter-test"
            />
            <FilterStat
                icon={Copy}
                label="Tekrar (dedup)"
                count={filtered?.duplicateCount || 0}
                description="Aynı ziyaretçi, aynı ürün, 5 dakika içinde tekrar açılış"
                testId="traffic-filter-dup"
            />
        </div>
    </Card>
);

const FilterStat = ({ icon: Icon, label, count, description, testId }) => (
    <div className="rounded-lg border border-border bg-muted/20 p-4" data-testid={testId}>
        <div className="flex items-center gap-1.5 mb-2">
            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
            <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                {label}
            </div>
        </div>
        <div className="font-display text-2xl font-black tracking-tighter num">
            {fmtInt(count)}
        </div>
        <div className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">
            {description}
        </div>
    </div>
);

/* ── Timeline chart ──────────────────────────────────────────────────── */
const TrafficTimelineCard = ({ timeline }) => {
    const hasData = Array.isArray(timeline) && timeline.some(
        (d) => (d.billed || 0) + (d.bot || 0) + (d.test || 0) + (d.dup || 0) > 0
    );
    const chartData = (timeline || []).map((d) => ({
        date: d.date?.slice(5) || "", // "MM-DD"
        Sayıldı: d.billed || 0,
        Bot: d.bot || 0,
        Test: d.test || 0,
        Dedup: d.dup || 0,
    }));

    return (
        <Card data-testid="traffic-timeline-card">
            <CardHeader
                title="Günlük trafik"
                desc="Bu ayın gün-gün dağılımı — faturaya giden ve filtrelenen hepsi birlikte"
            />
            <div className="p-5">
                {!hasData ? (
                    <div className="h-40 grid place-items-center text-xs text-muted-foreground" data-testid="traffic-timeline-empty">
                        Bu dönemde henüz veri yok.
                    </div>
                ) : (
                    <div style={{ width: "100%", height: 260 }}>
                        <ResponsiveContainer minWidth={0}>
                            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip
                                    contentStyle={{ fontSize: 11, borderRadius: 6 }}
                                    formatter={(v) => fmtInt(v)}
                                />
                                <Legend wrapperStyle={{ fontSize: 10 }} />
                                <Bar dataKey="Sayıldı" stackId="a" fill="currentColor" fillOpacity={0.85} />
                                <Bar dataKey="Bot" stackId="a" fill="currentColor" fillOpacity={0.35} />
                                <Bar dataKey="Test" stackId="a" fill="currentColor" fillOpacity={0.20} />
                                <Bar dataKey="Dedup" stackId="a" fill="currentColor" fillOpacity={0.10} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </Card>
    );
};

/* ── Usage policy quick-reference ────────────────────────────────────── */
const UsagePolicyCard = () => (
    <Card data-testid="traffic-policy-card">
        <CardHeader
            title="Ne sayılır, ne sayılmaz"
            desc="Faturalama politikası — hiçbir gizli kural yok"
        />
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
            <div>
                <div className="flex items-center gap-1.5 mb-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    <div className="font-semibold">Sayılır</div>
                </div>
                <ul className="space-y-1.5 text-xs text-muted-foreground leading-relaxed pl-5">
                    <li className="list-disc">Beden Tablosu modal/drawer açılışı</li>
                    <li className="list-disc">Bedenimi Bul modal/drawer açılışı</li>
                    <li className="list-disc text-foreground/70">
                        <span className="font-medium">Dedup kuralı:</span> aynı oturum + aynı ürün için 5 dakika içindeki tekrar açılışlar tek sayılır
                    </li>
                </ul>
            </div>
            <div>
                <div className="flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                    <div className="font-semibold">Sayılmaz</div>
                </div>
                <ul className="space-y-1.5 text-xs text-muted-foreground leading-relaxed pl-5">
                    <li className="list-disc">Widget script'in yüklenmesi (ürün sayfa ziyareti)</li>
                    <li className="list-disc">Önerilen beden gösterimi</li>
                    <li className="list-disc">Bot / crawler trafiği</li>
                    <li className="list-disc">Test ortamı (<code className="font-mono text-[11px]">?sayz_test=1</code>) ve panel önizleme</li>
                </ul>
            </div>
        </div>
    </Card>
);

/* ────────────────── Tab: Conversion ────────────────── */

const ConversionTab = ({ data }) => (
    <div className="space-y-5" data-testid="analytics-tab-conversion-panel">
        <FunnelSection funnel={data.funnel} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <UniqueVisitorFunnel funnel={data.funnel?.uniqueVisitors} />
            <ConfidenceCard distribution={data.confidenceDistribution} />
        </div>
    </div>
);

/* ────────────────── Tab: Products (hotspots) ────────────────── */

const ProductsTab = ({ data }) => (
    <div data-testid="analytics-tab-products-panel">
        <HotspotsSection hotspots={data.hotspots} />
    </div>
);

/* ────────────────── Tab: Visitors ────────────────── */

const VisitorsTab = ({ data }) => (
    <div data-testid="analytics-tab-visitors-panel">
        <BodyInsightsSection body={data.bodyInsights} />
    </div>
);

/* ────────────────── Tab: Integration ────────────────── */

const IntegrationTab = ({ data }) => (
    <div className="space-y-5" data-testid="analytics-tab-integration-panel">
        <IntegrationHealthSection health={data.integrationHealth} />
        {data.timeline?.hourHeatmap && (
            <Card>
                <CardHeader
                    title="Saat × gün ısı haritası"
                    desc="Ping yoğunluğu — kurulum stabilitesini ve trafik dağılımını birlikte izle (UTC)"
                />
                <div className="p-5 overflow-x-auto">
                    <HourHeatmap grid={data.timeline.hourHeatmap || []} />
                </div>
            </Card>
        )}
    </div>
);

/* ────────────────── KPI Grid ────────────────── */

const KpiGrid = ({ kpis }) => {
    if (!kpis) return null;
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="analytics-kpis">
            <Kpi
                icon={Signal}
                label="Ürün ping'i"
                value={fmtInt(kpis.totalQueries)}
                sub={`${pct(kpis.matchRate)} eşleşme`}
                testId="kpi-total-queries"
            />
            <Kpi
                icon={Users}
                label="Benzersiz ziyaretçi"
                value={fmtInt(kpis.uniqueVisitors)}
                sub="Bu dönem widget'ı gören tab"
                testId="kpi-unique-visitors"
            />
            <Kpi
                icon={MousePointerClick}
                label="Modal açılış"
                value={fmtInt(kpis.modalOpens)}
                sub="Beden Tablosu + Bedenimi Bul"
                testId="kpi-modal-opens"
            />
            <Kpi
                icon={CheckCircle2}
                label="Önerilen kabul"
                value={fmtInt(kpis.accepted)}
                sub={`${pct(kpis.acceptRate)} kabul oranı`}
                testId="kpi-accepted"
                tooltip={"\"Bedeni Seç\" CTA'sına tıklanan oturumlar. Satın alma değil — ziyaretçinin öneriyi benimseme sinyali."}
            />
            <Kpi
                icon={Gauge}
                label="Katalog kapsamı"
                value={pct(kpis.catalogCoverage)}
                sub={`${fmtInt(kpis.pingedSkus)}/${fmtInt(kpis.activeProducts)} aktif ürün`}
                testId="kpi-coverage"
            />
            <Kpi
                icon={ShieldAlert}
                label="JS hatası"
                value={fmtInt(kpis.errorCount)}
                sub={kpis.errorCount ? "İncele →" : "Temiz"}
                severity={kpis.errorCount > 0 ? "danger" : "ok"}
                testId="kpi-errors"
            />
        </div>
    );
};

const Kpi = ({ icon: Icon, label, value, sub, severity = "default", testId, tooltip }) => {
    const tone = severity === "danger"
        ? "text-destructive"
        : severity === "ok" ? "text-success" : "text-muted-foreground";
    return (
        <div className="rounded-xl border border-border bg-surface p-4" data-testid={testId}>
            <div className="flex items-center gap-1.5 mb-2">
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                    {label}
                </div>
                {tooltip && (
                    <span className="ml-auto" title={tooltip} data-testid={`${testId}-info`}>
                        <Info className="h-3 w-3 text-muted-foreground/70" />
                    </span>
                )}
            </div>
            <div className="font-display text-2xl font-black tracking-tighter num">{value}</div>
            {sub && <div className={`text-[11px] mt-0.5 num ${tone}`}>{sub}</div>}
        </div>
    );
};

/* ────────────────── Funnels ────────────────── */

const FunnelSection = ({ funnel }) => {
    if (!funnel) return null;
    const st = funnel.sizeTable || {};
    const fms = funnel.findMySize || {};

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="analytics-funnel">
            <Card>
                <CardHeader
                    title="Beden Tablosu funnel"
                    desc="Ürün sayfası ping → Beden Tablosu modal açılış"
                />
                <div className="p-5 space-y-3">
                    <FunnelBar label="Ürün ping" value={st.productPings} max={st.productPings} />
                    <FunnelBar label="Modal açılış" value={st.modalOpens} max={st.productPings} />
                    <FunnelRate
                        label="CTR"
                        value={rate(st.modalOpens, st.productPings)}
                    />
                </div>
            </Card>

            <Card>
                <CardHeader
                    title="Bedenimi Bul funnel"
                    desc="Ürün sayfası ping → modal → öneri → kabul"
                />
                <div className="p-5 space-y-3">
                    <FunnelBar label="Ürün ping" value={fms.productPings} max={fms.productPings} />
                    <FunnelBar label="Modal açılış" value={fms.modalOpens} max={fms.productPings} />
                    <FunnelBar label="Beden önerisi" value={fms.suggestions} max={fms.productPings} />
                    <FunnelBar label="Önerilen kabul" value={fms.accepted} max={fms.productPings} />
                    <div className="grid grid-cols-3 gap-3 pt-1">
                        <FunnelRate
                            label="CTR"
                            value={rate(fms.modalOpens, fms.productPings)}
                        />
                        <FunnelRate
                            label="Tamamlama"
                            value={rate(fms.suggestions, fms.modalOpens)}
                        />
                        <FunnelRate
                            label="Kabul"
                            value={rate(fms.accepted, fms.suggestions)}
                        />
                    </div>
                </div>
            </Card>
        </div>
    );
};

const UniqueVisitorFunnel = ({ funnel }) => {
    if (!funnel) return null;
    const total = funnel.pings || 0;
    return (
        <Card data-testid="analytics-unique-funnel">
            <CardHeader
                title="Benzersiz ziyaretçi funnel'i"
                desc="Her adımda farklı session_id sayısı — aynı ziyaretçi tekrar açılış şişirmez"
            />
            <div className="p-5 space-y-3">
                <FunnelBar label="Ping gören ziyaretçi" value={funnel.pings} max={total} />
                <FunnelBar label="Modal açan" value={funnel.modalOpens} max={total} />
                <FunnelBar label="Öneri alan" value={funnel.suggestions} max={total} />
                <FunnelBar label="Öneriyi kabul eden" value={funnel.accepted} max={total} />
                <div className="grid grid-cols-3 gap-3 pt-1">
                    <FunnelRate label="Açılış" value={rate(funnel.modalOpens, funnel.pings)} />
                    <FunnelRate label="Öneri" value={rate(funnel.suggestions, funnel.modalOpens)} />
                    <FunnelRate label="Kabul" value={rate(funnel.accepted, funnel.suggestions)} />
                </div>
            </div>
        </Card>
    );
};

const FunnelBar = ({ label, value, max }) => {
    const w = max > 0 ? Math.max(2, (value / max) * 100) : 0;
    return (
        <div>
            <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-medium">{label}</span>
                <span className="font-mono text-muted-foreground">{fmtInt(value)}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                    className="h-full bg-foreground transition-all"
                    style={{ width: `${w}%` }}
                />
            </div>
        </div>
    );
};

const FunnelRate = ({ label, value }) => (
    <div className="rounded-md bg-muted/40 px-3 py-2 flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">{label}</span>
        <span className="font-display font-bold text-sm num">{value}</span>
    </div>
);

/* ────────────────── Confidence distribution ────────────────── */

const ConfidenceCard = ({ distribution }) => {
    if (!distribution) return null;
    const buckets = distribution.buckets || [];
    const total = buckets.reduce((a, b) => a + (b.count || 0), 0);
    const avgPct = distribution.avg ? Math.round(distribution.avg * 100) : 0;

    if (!total) {
        return (
            <Card data-testid="analytics-confidence-empty" className="p-6 flex flex-col items-start gap-2">
                <Eyebrow>Öneri güveni</Eyebrow>
                <div className="font-display font-semibold text-sm">Henüz veri yok</div>
                <div className="text-xs text-muted-foreground">
                    Bedenimi Bul çalıştıkça algoritmanın güven dağılımı burada görünür.
                </div>
            </Card>
        );
    }

    const toneFor = (key) => key === "high"
        ? "bg-success/80"
        : key === "medium" ? "bg-warning/80" : "bg-destructive/80";

    return (
        <Card data-testid="analytics-confidence">
            <CardHeader
                title="Öneri güveni (confidence)"
                desc="Nearest-neighbor algoritmasının her öneri için ürettiği 0–1 güven skoru"
            />
            <div className="p-5">
                <div className="flex items-end gap-3 mb-4">
                    <div className="font-display text-4xl font-black tracking-tighter num" data-testid="analytics-confidence-avg">
                        {avgPct}%
                    </div>
                    <div className="text-xs text-muted-foreground pb-1.5">
                        ortalama · {fmtInt(total)} öneri üzerinden
                    </div>
                </div>

                {/* Stacked bar */}
                <div className="h-2 rounded-full bg-muted overflow-hidden flex mb-3">
                    {buckets.map((b) => {
                        const p = total ? (b.count / total) * 100 : 0;
                        return (
                            <div
                                key={b.key}
                                className={`${toneFor(b.key)} transition-all`}
                                style={{ width: `${p}%` }}
                                title={`${b.label} · ${fmtInt(b.count)} öneri`}
                            />
                        );
                    })}
                </div>

                <div className="space-y-2">
                    {buckets.map((b) => {
                        const p = total ? (b.count / total) * 100 : 0;
                        return (
                            <div key={b.key} className="flex items-center gap-3 text-xs" data-testid={`confidence-bucket-${b.key}`}>
                                <span className={`inline-block h-2 w-2 rounded-full ${toneFor(b.key)}`} />
                                <span className="font-medium capitalize min-w-[70px]">{b.label}</span>
                                <span className="text-muted-foreground">
                                    {b.key === "high" && "≥ %80"}
                                    {b.key === "medium" && "%60 – %80"}
                                    {b.key === "low" && "< %60"}
                                </span>
                                <span className="ml-auto font-mono text-muted-foreground">{fmtInt(b.count)}</span>
                                <span className="font-mono font-semibold w-10 text-right">
                                    {p.toFixed(0)}%
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Card>
    );
};

/* ────────────────── Timeline ────────────────── */

const TimelineSection = ({ timeline }) => {
    if (!timeline) return null;
    const daily = (timeline.daily || []).map((d) => ({
        ...d,
        date: d.date?.slice(5),  // MM-DD
    }));

    return (
        <Card data-testid="analytics-timeline">
            <CardHeader
                title="Günlük etkinlik"
                desc="Ping, öneri, kabul — gün bazında"
            />
            <div className="p-5 h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={daily} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
                        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                        <Tooltip content={<MiniTooltip />} cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "3 3" }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Area type="monotone" dataKey="queries" name="Ping" stroke="hsl(var(--brand-accent))" fill="hsl(var(--brand-accent) / 0.15)" strokeWidth={2} />
                        <Line type="monotone" dataKey="suggestions" name="Öneri" stroke="hsl(var(--foreground))" strokeWidth={1.5} dot={false} />
                        <Line type="monotone" dataKey="accepted" name="Kabul" stroke="hsl(var(--success))" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};

const DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const HourHeatmap = ({ grid }) => {
    const max = useMemo(
        () => Math.max(1, ...grid.flat()),
        [grid]
    );
    return (
        <div className="min-w-[520px]">
            <div className="grid grid-cols-[24px_repeat(24,minmax(0,1fr))] gap-[2px] items-center">
                <div />
                {Array.from({ length: 24 }).map((_, h) => (
                    <div key={h} className="text-[9px] text-muted-foreground text-center font-mono">
                        {h % 3 === 0 ? h : ""}
                    </div>
                ))}
                {grid.map((row, i) => (
                    <RowCells key={i} day={DAYS[i]} row={row} max={max} />
                ))}
            </div>
            <div className="flex items-center gap-1.5 mt-3 text-[10px] text-muted-foreground">
                <span>az</span>
                {[0.1, 0.3, 0.55, 0.8, 1].map((a) => (
                    <span
                        key={a}
                        className="h-2.5 w-4 rounded-sm"
                        style={{ background: `hsl(var(--brand-accent) / ${a})` }}
                    />
                ))}
                <span>çok</span>
            </div>
        </div>
    );
};

const RowCells = ({ day, row, max }) => (
    <>
        <div className="text-[9px] text-muted-foreground font-mono pr-1">{day}</div>
        {row.map((v, h) => {
            const alpha = v > 0 ? Math.max(0.08, v / max) : 0;
            return (
                <div
                    key={h}
                    className="aspect-square rounded-[2px] border border-border/40"
                    style={{
                        background: v > 0
                            ? `hsl(var(--brand-accent) / ${alpha})`
                            : "hsl(var(--muted) / 0.3)",
                    }}
                    title={`${day} ${h}:00 — ${v} ping`}
                />
            );
        })}
    </>
);

/* ────────────────── Hotspots ────────────────── */

const HotspotsSection = ({ hotspots }) => {
    if (!hotspots) return null;
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="analytics-hotspots">
            <HotspotTable
                title="En çok sorgulanan ürünler"
                desc="Ziyaretçi tarafından widget çağrılan ürünler"
                icon={Flame}
                columns={["SKU", "Ping", "Eşleşen"]}
                rows={(hotspots.topQueried || []).map((r) => [
                    r.sku, fmtInt(r.count), r.matched === r.count
                        ? <span className="text-success num">{fmtInt(r.matched)}</span>
                        : <span className="text-warning num">{fmtInt(r.matched)}</span>,
                ])}
                testId="hotspot-top-queried"
                emptyText="Henüz ping yok."
            />

            <HotspotTable
                title="Aralık dışı sıcak noktalar"
                desc="Ziyaretçi ölçüsü tablonun dışına düşen ürünler — bedenlerinizi genişletmeyi düşünün"
                icon={AlertTriangle}
                columns={["SKU", "Oran", "Toplam"]}
                rows={(hotspots.outOfRangeTop || []).map((r) => [
                    r.sku,
                    <span className="font-medium num text-warning">{pct(r.rate)}</span>,
                    fmtInt(r.total),
                ])}
                testId="hotspot-oor"
                emptyText="Harika — tüm öneriler aralık içi."
            />

            <HotspotTable
                title="Düşük kabul oranları"
                desc="Çok açılıp öneri kabul edilmeyen ürünler — tablo/fit kontrolü önerilir"
                icon={TrendingDown}
                columns={["SKU", "Açılış", "Kabul", "Oran"]}
                rows={(hotspots.lowAcceptTop || []).map((r) => [
                    r.sku, fmtInt(r.opens), fmtInt(r.accepts),
                    <span className="font-medium num text-warning">{pct(r.rate)}</span>,
                ])}
                testId="hotspot-low-accept"
                emptyText="Henüz yeterli sinyal yok."
            />

            <HotspotTable
                title="Karanlık katalog"
                desc={`Hiç ping almayan aktif ürünler · toplam ${fmtInt(hotspots.darkCatalogTotal || 0)}`}
                icon={Package}
                columns={["SKU", "Ürün"]}
                rows={(hotspots.darkCatalog || []).map((p) => [
                    p.id, p.name || "—",
                ])}
                testId="hotspot-dark-catalog"
                emptyText="Tüm aktif ürünlerde en az bir ping var."
            />
        </div>
    );
};

const HotspotTable = ({ title, desc, icon: Icon, columns, rows, testId, emptyText }) => (
    <Card data-testid={testId}>
        <CardHeader
            title={
                <span className="inline-flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    {title}
                </span>
            }
            desc={desc}
        />
        {rows.length === 0 ? (
            <div className="px-5 py-8 text-center text-xs text-muted-foreground">{emptyText}</div>
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b border-border">
                            {columns.map((c) => (
                                <th key={c} className="px-5 py-2.5 text-left text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                                    {c}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((cells, i) => (
                            <tr key={i} className="border-b border-border/60 last:border-0">
                                {cells.map((cell, j) => (
                                    <td
                                        key={j}
                                        className={`px-5 py-2.5 ${j === 0 ? "font-mono text-foreground" : ""}`}
                                    >
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </Card>
);

/* ────────────────── Body insights ────────────────── */

const BodyInsightsSection = ({ body }) => {
    if (!body) return null;
    const sugRows = (body.suggestedSizes || []).slice(0, 12);
    const fitRows = body.fitPreference || [];
    const shapes = body.shapes || {};

    const hasBodyData =
        sugRows.length || fitRows.length ||
        Object.values(shapes).some((arr) => (arr || []).length) ||
        (body.heightWeight || []).length;

    if (!hasBodyData) {
        return (
            <Card data-testid="analytics-body-insights-empty" className="p-8 text-center">
                <Users className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                <div className="font-display font-semibold text-sm">Vücut profili verisi yok</div>
                <div className="text-xs text-muted-foreground mt-1">
                    Ziyaretçiler Bedenimi Bul widget'ını kullandıkça bu alanda dağılım grafikleri belirir.
                </div>
            </Card>
        );
    }

    return (
        <div className="space-y-5" data-testid="analytics-body-insights">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card>
                    <CardHeader
                        title="Önerilen bedenler"
                        desc="En çok önerilen bedenler — stok dağılımı için referans"
                    />
                    <div className="p-5 h-[220px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={sugRows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="size" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} />
                                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                                <Tooltip content={<MiniTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.5)" }} />
                                <Bar dataKey="count" name="Öneri" fill="hsl(var(--brand-accent))" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card>
                    <CardHeader
                        title="Fit tercihi dağılımı"
                        desc="Ziyaretçinin seçtiği kalıp tipi"
                    />
                    <div className="p-5">
                        {fitRows.length === 0 ? (
                            <div className="text-xs text-muted-foreground text-center py-8">Henüz veri yok.</div>
                        ) : (
                            <div className="space-y-2">
                                {fitRows.map((r) => (
                                    <StackRow key={r.fit} label={r.fit} value={r.count} total={fitRows.reduce((a, b) => a + b.count, 0)} />
                                ))}
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            <Card>
                <CardHeader
                    title="Vücut ölçüleri dağılımı"
                    desc="Göğüs, bel, kalça, omuz — ziyaretçilerin seçtiği şekiller"
                />
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {["chest", "waist", "hip", "shoulder"].map((k) => (
                        <ShapeBreakdown
                            key={k}
                            label={SHAPE_LABELS[k]}
                            rows={shapes[k] || []}
                        />
                    ))}
                </div>
            </Card>

            {(body.heightWeight || []).length > 0 && (
                <Card>
                    <CardHeader
                        title="Boy × kilo yoğunluğu"
                        desc="10cm × 10kg kovaları — kataloğunuzun hedef kitlesi"
                    />
                    <div className="p-5 overflow-x-auto">
                        <HeightWeightGrid data={body.heightWeight} />
                    </div>
                </Card>
            )}
        </div>
    );
};

const SHAPE_LABELS = {
    chest: "Göğüs",
    waist: "Bel",
    hip: "Kalça",
    shoulder: "Omuz",
};

const ShapeBreakdown = ({ label, rows }) => {
    const total = rows.reduce((a, b) => a + b.count, 0);
    return (
        <div>
            <Eyebrow>{label}</Eyebrow>
            {rows.length === 0 ? (
                <div className="text-xs text-muted-foreground mt-2">—</div>
            ) : (
                <div className="space-y-1.5 mt-2">
                    {rows.slice(0, 6).map((r) => (
                        <StackRow key={r.value} label={r.value} value={r.count} total={total} />
                    ))}
                </div>
            )}
        </div>
    );
};

const StackRow = ({ label, value, total }) => {
    const p = total > 0 ? (value / total) * 100 : 0;
    return (
        <div className="flex items-center gap-2">
            <div className="text-[11px] font-medium capitalize w-16 shrink-0">{label}</div>
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-foreground" style={{ width: `${p}%` }} />
            </div>
            <div className="text-[10px] font-mono text-muted-foreground w-10 text-right">{fmtInt(value)}</div>
        </div>
    );
};

const HeightWeightGrid = ({ data }) => {
    // Build 2d array: height rows (140-210 by 10) × weight cols (30-150 by 10)
    const heights = [];
    for (let h = 140; h <= 210; h += 10) heights.push(h);
    const weights = [];
    for (let w = 30; w <= 150; w += 10) weights.push(w);
    const map = {};
    data.forEach((d) => { map[`${d.height}-${d.weight}`] = d.count; });
    const max = Math.max(1, ...data.map((d) => d.count));

    return (
        <div className="inline-block">
            <div
                className="grid gap-[2px]"
                style={{ gridTemplateColumns: `44px repeat(${weights.length}, 24px)` }}
            >
                <div />
                {weights.map((w) => (
                    <div key={w} className="text-[9px] font-mono text-muted-foreground text-center">{w}</div>
                ))}
                {heights.map((h) => (
                    <RowHW key={h} h={h} weights={weights} map={map} max={max} />
                ))}
            </div>
            <div className="text-[10px] text-muted-foreground mt-2 font-mono">
                X: kilo (kg) · Y: boy (cm)
            </div>
        </div>
    );
};

const RowHW = ({ h, weights, map, max }) => (
    <>
        <div className="text-[9px] font-mono text-muted-foreground pr-1 text-right flex items-center justify-end">{h}</div>
        {weights.map((w) => {
            const v = map[`${h}-${w}`] || 0;
            const alpha = v > 0 ? Math.max(0.1, v / max) : 0;
            return (
                <div
                    key={w}
                    className="h-6 rounded-[2px] border border-border/40"
                    style={{
                        background: v > 0 ? `hsl(var(--brand-accent) / ${alpha})` : "hsl(var(--muted) / 0.3)",
                    }}
                    title={`${h}cm × ${w}kg — ${v} ziyaretçi`}
                />
            );
        })}
    </>
);

/* ────────────────── Integration Health ────────────────── */

const IntegrationHealthSection = ({ health }) => {
    if (!health) return null;
    const methods = health.methods || [];
    const methodTotal = methods.reduce((a, b) => a + b.count, 0);
    const errorDaily = (health.errorDaily || []).map((d) => ({ ...d, date: d.date?.slice(5) }));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="analytics-integration-health">
            <Card>
                <CardHeader
                    title="SKU algılama yöntemi"
                    desc="meta/jsonld/url — hangi kaynaktan okundu"
                />
                <div className="p-5">
                    {methods.length === 0 ? (
                        <div className="text-xs text-muted-foreground text-center py-6">Henüz veri yok.</div>
                    ) : (
                        <div className="space-y-2">
                            {methods.map((m) => (
                                <StackRow key={m.method} label={m.method} value={m.count} total={methodTotal} />
                            ))}
                        </div>
                    )}
                </div>
            </Card>

            <Card>
                <CardHeader
                    title="Origin eşleşmesi"
                    desc="Site domain ile widget origin'i uyumlu mu?"
                />
                <div className="p-5 flex flex-col items-center justify-center h-full">
                    <div className="font-display text-5xl font-black tracking-tighter num">
                        {pct(health.originOkRate)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 num">
                        {fmtInt(health.originOkCount)} eşleşen istek
                    </div>
                    {health.originOkRate < 0.8 && (health.originOkCount > 0 || methodTotal > 0) && (
                        <div className="mt-3 text-[11px] text-warning text-center max-w-xs">
                            Origin eşleşmeleri düşük — kurulumda site_domain alanını kontrol edin.
                        </div>
                    )}
                </div>
            </Card>

            <Card>
                <CardHeader
                    title="JS hata trendi"
                    desc="Gün bazında ziyaretçi tarafında yakalanan hata"
                />
                <div className="p-5 h-[160px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={errorDaily} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
                            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={false} />
                            <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip content={<MiniTooltip />} cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "3 3" }} />
                            <Line type="monotone" dataKey="count" name="Hata" stroke="hsl(var(--destructive))" strokeWidth={1.5} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>
        </div>
    );
};

/* ────────────────── Shared ────────────────── */

const MiniTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-md border border-border bg-popover/95 backdrop-blur px-3 py-2 shadow-md text-xs">
            <div className="text-[10px] font-mono text-muted-foreground mb-1">{label}</div>
            {payload.map((p) => (
                <div key={p.dataKey} className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
                        <span className="text-muted-foreground">{p.name || p.dataKey}</span>
                    </span>
                    <span className="ml-auto font-mono font-medium">{p.value}</span>
                </div>
            ))}
        </div>
    );
};

const fmtInt = (n) => (typeof n === "number" ? n.toLocaleString("tr-TR") : "0");
const pct = (n) => {
    if (typeof n !== "number" || !isFinite(n)) return "0%";
    return `${(n * 100).toFixed(n >= 0.1 ? 0 : 1)}%`;
};
const rate = (num, den) => (den > 0 ? pct(num / den) : "—");
